import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Send, Loader2, MessageSquareShare } from 'lucide-react';
import * as Sentry from '@sentry/react';
import { cn } from '@/lib/utils';
import ReactMarkdown from 'react-markdown';
import { streamExternalChat } from '@/services/external-chat/stream';
import { useEmbedStyles } from '@/features/external-chat/hooks/use-embed-styles';
import { isNavigableHref } from '@/utils/url-utils';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isStreaming: boolean;
  status?: string;
}

interface EmbedChatPageProps {
  seedQuery?: string;
  seedSummary?: string;
  searchContext?: string;
  embedded?: boolean;
}

/**
 * Minimal full-viewport chat page designed to be rendered inside an iframe
 * for external embedding. No platform nav, sidebar, or auth required.
 * Authentication is handled by the embed API key passed as a URL query param.
 */
export const EmbedChatPage = ({
  seedQuery,
  seedSummary,
  searchContext,
  embedded,
}: EmbedChatPageProps = {}) => {
  const { orgId, collectionId } = useParams<{
    orgId: string;
    collectionId: string;
  }>();
  const [searchParams] = useSearchParams();

  const apiKey = searchParams.get('apiKey') || '';
  const primaryColor = searchParams.get('primaryColor') || '#2563eb';
  const fontColor = searchParams.get('fontColor') || '#111827';
  const bgColor = searchParams.get('bgColor') || '#ffffff';
  const linkColor = searchParams.get('linkColor') || primaryColor;
  const greeting = searchParams.get('greeting') || '';
  const title = searchParams.get('title') || 'Chat';

  const headerFont = searchParams.get('headerFont') || '';
  const headerFontWeight = searchParams.get('headerFontWeight') || '';
  const bodyFont = searchParams.get('bodyFont') || '';
  const bodyFontWeight = searchParams.get('bodyFontWeight') || '';
  const searchBgColor = searchParams.get('searchBgColor') || '';
  const searchTextColor = searchParams.get('searchTextColor') || '';
  const customCss = searchParams.get('customCss') || '';
  const borderRadius = searchParams.get('borderRadius') || '8px';
  const contentMaxWidth = searchParams.get('contentMaxWidth') || '';

  const [messages, setMessages] = useState<ChatMessage[]>(() => {
    const initial: ChatMessage[] = [];
    if (seedQuery && seedSummary) {
      initial.push(
        {
          id: 'seed-user',
          role: 'user',
          content: seedQuery,
          isStreaming: false,
        },
        {
          id: 'seed-assistant',
          role: 'assistant',
          content: seedSummary,
          isStreaming: false,
        },
      );
    } else if (greeting) {
      initial.push({
        id: 'greeting',
        role: 'assistant',
        content: greeting,
        isStreaming: false,
      });
    }
    return initial;
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | undefined>();

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sendingRef = useRef(false);

  useEmbedStyles({
    headerFont,
    headerFontWeight,
    bodyFont,
    bodyFontWeight,
    customCss,
    scope: 'chat',
  });

  // Auto-scroll only when user is already near the bottom (avoids scroll jank during streaming)
  const scrollToBottom = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const isNearBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight <
      100;
    if (isNearBottom) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const headerStyle = useMemo(
    () => ({ backgroundColor: primaryColor }),
    [primaryColor],
  );

  const sendButtonStyle = useMemo(
    () => ({ backgroundColor: primaryColor }),
    [primaryColor],
  );

  const pageStyle = useMemo(
    () => ({
      backgroundColor: bgColor,
      color: fontColor,
      fontFamily: bodyFont || undefined,
      fontWeight: bodyFontWeight || undefined,
    }),
    [bgColor, fontColor, bodyFont, bodyFontWeight],
  );

  const inputBarStyle = useMemo(
    () => ({ backgroundColor: bgColor, borderColor: fontColor + '20' }),
    [bgColor, fontColor],
  );

  const handleSend = useCallback(async () => {
    const trimmed = input.trim();
    if (!trimmed || sendingRef.current || !orgId || !collectionId || !apiKey)
      return;

    sendingRef.current = true;
    setInput('');
    setIsLoading(true);

    const userMessageId = `user-${crypto.randomUUID()}`;
    const assistantMessageId = `assistant-${crypto.randomUUID()}`;

    // Add user message
    const userMessage: ChatMessage = {
      id: userMessageId,
      role: 'user',
      content: trimmed,
      isStreaming: false,
    };

    // Add empty assistant message (streaming placeholder)
    const assistantMessage: ChatMessage = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);

    // Abort any in-flight request
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    await streamExternalChat({
      orgId,
      collectionId,
      apiKey,
      message: trimmed,
      conversationId,
      searchContext: !conversationId ? searchContext : undefined,
      signal: controller.signal,
      onToken: (token) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, content: msg.content + token, status: undefined }
              : msg,
          ),
        );
      },
      onStatus: (status) => {
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId ? { ...msg, status } : msg,
          ),
        );
      },
      onError: (error) => {
        Sentry.captureException(new Error(error), {
          extra: { context: 'embed-chat-stream', orgId, collectionId },
        });
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? {
                  ...msg,
                  content:
                    msg.content ||
                    'Sorry, something went wrong. Please try again.',
                  isStreaming: false,
                }
              : msg,
          ),
        );
        sendingRef.current = false;
        setIsLoading(false);
      },
      onDone: (_fullMessage, newConversationId) => {
        setConversationId(newConversationId || undefined);
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === assistantMessageId
              ? { ...msg, isStreaming: false }
              : msg,
          ),
        );
        sendingRef.current = false;
        setIsLoading(false);
      },
    });
  }, [input, orgId, collectionId, apiKey, conversationId, searchContext]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  // Auto-resize textarea
  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setInput(e.target.value);
      const textarea = e.target;
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    },
    [],
  );

  const isSendDisabled = useMemo(
    () => !input.trim() || isLoading || !apiKey,
    [input, isLoading, apiKey],
  );

  // Error state: missing params
  if (!orgId || !collectionId) {
    return (
      <div className='flex h-dvh items-center justify-center bg-white p-4 text-center text-sm text-gray-500'>
        Missing required parameters (orgId, collectionId).
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className='flex h-dvh items-center justify-center bg-white p-4 text-center text-sm text-gray-500'>
        Missing API key. Add <code className='text-xs'>?apiKey=eak_...</code> to
        the URL.
      </div>
    );
  }

  return (
    <div
      className={cn('flex flex-col', embedded ? 'h-full' : 'h-dvh')}
      style={pageStyle}
    >
      {/* Header — only when standalone (not embedded inside search page) */}
      {!embedded && (
        <header
          className='flex shrink-0 items-center gap-2 px-4 py-3 text-white shadow-sm'
          style={headerStyle}
        >
          <MessageSquareShare className='size-5' />
          <h1
            className='text-sm font-semibold'
            style={{
              fontFamily: headerFont || undefined,
              fontWeight: headerFontWeight || undefined,
            }}
          >
            {title}
          </h1>
        </header>
      )}

      {/* Messages */}
      <div
        ref={scrollContainerRef}
        className='flex-1 overflow-y-auto px-4 py-4'
      >
        {messages.length === 0 && (
          <div className='flex h-full items-center justify-center'>
            <p className='text-sm text-gray-400'>
              Send a message to start the conversation.
            </p>
          </div>
        )}

        <div
          className={`mx-auto space-y-4 ${contentMaxWidth ? '' : 'max-w-2xl'}`}
          style={contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined}
        >
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={cn(
                'flex',
                msg.role === 'user' ? 'justify-end' : 'justify-start',
              )}
            >
              <div
                className={cn(
                  'max-w-[85%] px-4 py-2.5 text-sm',
                  msg.role === 'user' ? 'bg-black/5' : '',
                )}
                style={{ color: fontColor, borderRadius }}
              >
                {msg.role === 'assistant' ? (
                  <>
                    {msg.status && !msg.content && (
                      <div className='flex items-center gap-2 py-1'>
                        <Loader2 className='size-4 animate-spin opacity-50' />
                        <span className='text-xs italic opacity-60'>
                          {msg.status}
                        </span>
                      </div>
                    )}
                    {msg.content ? (
                      <div className='prose prose-sm max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0'>
                        <ReactMarkdown
                          components={{
                            // A destination react-markdown blanked, or one
                            // that resolves off this origin, is not a link:
                            // rendering it as an anchor would reopen the
                            // current page or jump off-site.
                            a: ({ href, children }) =>
                              isNavigableHref(href) ? (
                                <a
                                  href={href}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  style={{
                                    color: linkColor,
                                    textDecorationColor: linkColor + '50',
                                  }}
                                  className='underline underline-offset-2 hover:opacity-80'
                                >
                                  {children}
                                </a>
                              ) : (
                                <span>{children}</span>
                              ),
                          }}
                        >
                          {msg.content}
                        </ReactMarkdown>
                      </div>
                    ) : msg.isStreaming && !msg.status ? (
                      <div className='flex items-center gap-2 py-1'>
                        <Loader2 className='size-4 animate-spin text-gray-400' />
                        <span className='text-xs text-gray-400'>
                          Thinking...
                        </span>
                      </div>
                    ) : null}
                    {msg.isStreaming && msg.content && (
                      <span className='inline-block size-1.5 animate-pulse rounded-full bg-gray-400 align-middle' />
                    )}
                  </>
                ) : (
                  <p className='whitespace-pre-wrap'>{msg.content}</p>
                )}
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className='shrink-0 border-t px-4 py-3' style={inputBarStyle}>
        <div
          className={`mx-auto flex items-end gap-2 ${contentMaxWidth ? '' : 'max-w-2xl'}`}
          style={contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder='Type a message...'
            rows={1}
            disabled={isLoading}
            className='flex-1 resize-none border px-4 py-2.5 text-sm outline-none transition-colors disabled:opacity-50'
            style={{
              color: searchTextColor || fontColor,
              borderColor: fontColor + '30',
              backgroundColor: searchBgColor || fontColor + '08',
              borderRadius,
            }}
          />
          <button
            type='button'
            onClick={handleSend}
            disabled={isSendDisabled}
            className='flex size-10 shrink-0 items-center justify-center text-white transition-opacity disabled:opacity-40'
            style={{ ...sendButtonStyle, borderRadius }}
            aria-label='Send message'
          >
            <Send className='size-4' />
          </button>
        </div>
      </div>
    </div>
  );
};
